import {tryExecution, watchScreenWidth} from '../_util';
import Dropdown from 'bootstrap/js/dist/dropdown';
import {debounce, random} from 'lodash-es';

const baseClass = 'dropdown-menu';
const baseClassItem = 'dropdown-item';

const breakpointColums = 992;

let createdMenus = [];
let updateCallback = null;

export class DropdownMenuNested {
  /**
   * @type {HTMLElement}
   * @private
   */
  _el;

  /**
   * @type {string|null}
   * @private
   */
  _activeGroup = null;

  /**
   * @type {HTMLElement}
   * @private
   */
  _dropdownEl;

  /**
   * @type {HTMLElement}
   * @private
   */
  _dropdownToggleEl;

  /**
   * @type {Dropdown}
   * @private
   */
  _dropdown;

  /**
   * @type {boolean}
   * @private
   */
  _allowFullSize = false;

  /**
   * @type {boolean}
   * @private
   */
  _dropdownExtended = false;

  /**
   * @type {(() => void)[]}
   * @private
   */
  _openCallbacks = [];

  constructor(el) {
    this._el = el;
    this._dropdownEl = this._el.closest('.dropdown');
    if (this._dropdownEl == null) {
      console.warn('Unable to find closest .dropdown for bootstraps dropdown feature.');

      return;
    }

    this._dropdownToggleEl = this._dropdownEl.querySelector('[data-bs-toggle="dropdown"]');
    if (this._dropdownToggleEl == null) {
      console.warn('Unable to find [data-bs-toggle="dropdown"] in .dropdown for bootstraps dropdown feature.');

      return;
    }

    this._dropdown = Dropdown.getOrCreateInstance(this._dropdownToggleEl);

    this._allowFullSize = this._el.closest(`.nav-pills--allow-full-size`) != null;

    watchScreenWidth(breakpointColums, (isBelow) => {
      const mobileClasses = [
        `${baseClass}--show-only-current`,
        this._allowFullSize ? `${baseClass}--full-size` : null,
      ].filter(x => x != null);

      if (isBelow) {
        this._el.classList.add(...mobileClasses);
      } else {
        this._el.classList.remove(...mobileClasses);
      }
    });

    this.attachEvents();
  }

  attachEvents() {
    this._dropdownEl.addEventListener('hidden.bs.dropdown', this.handleDropdownClose.bind(this));
    this._dropdownEl.addEventListener('show.bs.dropdown', () => {
      this.extendDropdown();
      this.openFirstGroup(true);
    });

    this._el.addEventListener('click', this.handleClick.bind(this));

    document.addEventListener('close-dropdowns', ({ target }) => {
      if (target.contains(this._dropdownEl) === false) {
        return;
      }

      this._dropdown.hide();
    });
  }

  extendDropdown() {
    if (this._dropdownExtended) {
      return;
    }

    for (const openCallback of this._openCallbacks) {
      openCallback();
    }

    this.createBackButtons();
    this.createActionBar();

    this._dropdownExtended = true;
  }

  createActionBar() {
    const actions = document.createElement('div');
    actions.classList.add(`${baseClass}__actions`);

    const closeAction = document.createElement('button');
    closeAction.type = "button";
    closeAction.classList.add('dropdown-menu__close-link');

    const closeIcon = document.createElement('i');
    closeIcon.classList.add(...[
      'fa',
      'fa-close'
    ]);
    closeAction.append(closeIcon);

    actions.append(closeAction);

    this._el.prepend(actions);
  }

  createBackButtons() {
    const groups = this._el.querySelectorAll(`.${baseClass}__group[data-group-name]`);

    for (const group of groups) {
      const node = document.createElement('a');
      node.classList.add(...[
        'dropdown-item',
        'dropdown-item--back-link'
      ]);
      node.innerText = "zurück";
      node.href = '#';

      group.prepend(node);
    }
  }

  handleDropdownClose() {
    this.activeGroup = null;
  }

  handleClick(event) {
    this.handleForwardsClick(event);
    this.handleBackwardsClick(event);
  }

  handleForwardsClick(event) {
    const { target } = event;

    if (target.classList.contains(`${baseClassItem}--nested-link`) === false) {
      return;
    }

    const { targetGroup } = target.dataset;
    if (targetGroup == null) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.activeGroup = targetGroup;
  }

  handleBackwardsClick(event) {
    const { target } = event;

    if (target.classList.contains(`${baseClassItem}--back-link`) === false) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.activeGroup = this.getParentGroupOfGroup(this.activeGroup);
  }

  openGroupByName(groupName, isCurrent) {
    if (groupName == null) {
      this.openFirstGroup(isCurrent);

      return;
    }

    const groupElement = this._el.querySelector(`.${baseClass}__group[data-group-name="${CSS.escape(groupName)}"]`);
    if (groupElement == null) {
      console.warn('Unable to find group with name', groupName);

      return;
    }

    this.openGroupWithColumn(groupElement, isCurrent);

    // Find link that could open this group
    const openerLinks = this._el.querySelectorAll(`.${baseClassItem}--nested-link[data-target-group="${CSS.escape(groupName)}"]`);
    if (openerLinks == null || openerLinks.length <= 0) {
      console.warn('Could not find opener link of group Name', groupName);

      return;
    }

    for (const openerLink of openerLinks) {
      openerLink.classList.add(`${baseClassItem}--nested-link-active`);
    }

    this.openGroupByName(this.getParentGroupOfGroup(groupName), false);
  }

  getParentGroupOfGroup(groupName) {
    // Find link that could open this group
    const openerLinks = this._el.querySelector(`.${baseClassItem}--nested-link[data-target-group="${CSS.escape(groupName)}"]`);
    if (openerLinks == null) {
      console.warn('Could not find opener link of group Name', groupName);

      return null;
    }

    const linkGroupElement = openerLinks.closest(`.${baseClass}__group`);
    if (linkGroupElement == null) {
      console.warn('Unable to find parent group of opener link with group name', groupName);

      return null;
    }

    const { groupName: openerGroupName } = linkGroupElement.dataset;
    // Convert explicitly undefined to null
    if (openerGroupName == null) {
      return null;
    }

    return openerGroupName;
  }

  openGroupWithColumn(groupElement, isCurrent) {
    const columnElement = groupElement.closest(`.${baseClass}__column`);
    if (columnElement == null) {
      console.warn('Unable to find parent column of group-element', groupElement);

      return;
    }

    groupElement.classList.add(`${baseClass}__group--visible`);
    columnElement.classList.add(`${baseClass}__column--visible`);

    if (isCurrent) {
      groupElement.classList.add(`${baseClass}__group--current`);
      columnElement.classList.add(`${baseClass}__column--current`);
    }
  }

  closeGroups() {
    function removeFromElements(elements, ...classNames) {
      for (const element of [ ...elements]) {
        element.classList.remove(...classNames);
      }
    }

    removeFromElements(
      this._el.getElementsByClassName(`${baseClassItem}--nested-link-active`),
      `${baseClassItem}--nested-link-active`
    );

    removeFromElements(
      this._el.getElementsByClassName(`${baseClass}__group`),
      `${baseClass}__group--visible`, `${baseClass}__group--current`
    );

    removeFromElements(
      this._el.getElementsByClassName(`${baseClass}__column`),
      `${baseClass}__column--visible`, `${baseClass}__column--current`
    );
  }

  openFirstGroup(isCurrent) {
    const firstGroup = this._el.querySelector(`.${baseClass}__group:first-child`);
    if (firstGroup == null) {
      return;
    }

    this.openGroupWithColumn(firstGroup, isCurrent);
  }

  /**
   * @param {string} title
   * @param {boolean} active
   * @param {boolean} hidden
   * @param {HTMLElement} dropdown
   */
  mergeDropdownIntoDropdown(title, active, hidden, dropdown) {
    const groupNamePrefix = `og-${random(1000, 9999)}`;
    this.addNestedLinkToFirstGroup(title, active, hidden, groupNamePrefix);

    const columns = dropdown.getElementsByClassName(`${baseClass}__column`);
    for (let i = 0; i < columns.length; i++) {
      const groups = columns[i].getElementsByClassName(`${baseClass}__group`);
      for (const group of groups) {
        const clonedGroup = group.cloneNode(true);

        // Prefix name in group element
        let groupName = clonedGroup.dataset.groupName;
        if (groupName != null) {
          groupName = `${groupNamePrefix}-${groupName}`;
        } else {
          groupName = groupNamePrefix;
        }
        clonedGroup.dataset.groupName = groupName;

        // prefix groupname of all links
        const targetLinks = clonedGroup.querySelectorAll('[data-target-group]');
        for (const targetLink of targetLinks) {
          targetLink.dataset.targetGroup = `${groupNamePrefix}-${targetLink.dataset.targetGroup}`;
        }

        this.addGroupToColumnIndex(i + 1, clonedGroup);
      }
    }
  }

  /**
   * @param {number} columnIndex
   * @param {HTMLElement} groupElement
   */
  addGroupToColumnIndex(columnIndex, groupElement) {
    const columns = this._el.getElementsByClassName(`${baseClass}__column`);
    if (columns.length <= columnIndex) {
      const createdColumn = document.createElement('div');
      createdColumn.classList.add(`${baseClass}__column`);

      for (let i = columns.length; i <= columnIndex; i++) {
        this._el.append(createdColumn.cloneNode());
      }
    }

    const column = columns[columnIndex];
    groupElement.classList.remove(
      `${baseClass}__group--visible`,
      `${baseClass}__group--current`,
    );

    column.append(groupElement);
  }

  addNestedLinkToFirstGroup(title, active, hidden, groupName) {
    const parentLink = document.createElement('a');
    parentLink.classList.add(...[
      'dropdown-item',
      'dropdown-item--nested-link',
      'dropdown-item--overflow-item',
      active ? 'active' : null,
      hidden ? 'dropdown-item--overflow-visible' : null,
    ].filter(x => x != null));

    parentLink.href = '#';
    parentLink.innerText = title;
    parentLink.dataset.targetGroup = groupName;

    this.addElementToFirstGroup(parentLink);
  }

  addElementToFirstGroup(element) {
    let column = this._el.querySelector(`.${baseClass}__column`);
    if (column == null) {
      column = document.createElement('div');
      column.classList.add(`${baseClass}__column`);

      const createdGroup = document.createElement('div');
      createdGroup.classList.add(`${baseClass}__group`);
      column.append(createdGroup);

      this._el.append(column);
    }

    const group = column.querySelector(`.${baseClass}__group`);
    group.prepend(element);
  }

  update() {
    this._dropdown.update();
  }

  onOpenDropdown(callback) {
    this._openCallbacks.push(callback);
  }

  /**
   * @param {null|string} group
   */
  set activeGroup(group) {
    if (this._activeGroup === group) {
      return;
    }

    this.closeGroups();

    this._activeGroup = group;
    this.openGroupByName(group, true);

    this.update();
  }

  /**
   * @return {null|string}
   */
  get activeGroup() {
    return this._activeGroup;
  }
}

function createMenuInstance(element) {
  const existingElement = getMenuInstance(element);

  if (existingElement != null) {
    return existingElement;
  }

  const menu = tryExecution(() => new DropdownMenuNested(element));
  createdMenus.push({
    menuElement: element,
    menu: menu,
  });

  return menu;
}

/**
 * @param {HTMLElement} element
 * @returns {DropdownMenuNested|null}
 */
function getMenuInstance(element) {
  const result = createdMenus.find(({ menuElement }) => menuElement === element);
  if (result == null) {
    return null;
  }

  return result.menu;
}

function init() {
  const menus = document.getElementsByClassName(`${baseClass}--nested`);
  updateCallback = debounce(() => {
    for (const { menu } of createdMenus) {
      menu.update();
    }
  }, 300);

  /**
   * @type {DropdownMenuNested[]}
   */
  createdMenus = Array.prototype.map.call(
    menus,
    (menu) => ({
      menuElement: menu,
      menu: tryExecution(() => new DropdownMenuNested(menu)),
    }),
  );

  window.addEventListener('resize', updateCallback);
}

export function updateDropdownMenus() {
  if (updateCallback == null) {
    return;
  }

  updateCallback();
}

export default {
  init,
  getMenuInstance,
  createMenuInstance,
};
